/* =====================================================================================
   Create forms for Channel, Studio, Contracts and Ledger.

   One file because they are the same shape: a modal, a handful of fields, a save that
   reports what the database said rather than swallowing it. Kept out of the module files so
   those stay readable.

   Every counterparty is chosen from the CRM account list, or created inline. That is the
   difference between a system where "Acme Diagnostics" is one company and one where it is
   four slightly different strings nobody can reconcile at year end.
   ===================================================================================== */

import { useEffect, useState, type ReactNode } from 'react';
import {
  fetchAccounts, createAccount, createPartner, createTerritoryGrant, createTender,
  createAsset, createContract, createInvoice, nextInvoiceNumber,
  ACCOUNT_KINDS, PARTNER_TYPES, EXCLUSIVITY, ASSET_TYPES, CONTRACT_TYPES,
  type Account,
} from '../lib/commercial';
import {
  fetchStages, createOpportunity, logInteraction,
  INTERACTION_KINDS, SENTIMENTS, type Stage,
} from '../lib/pipeline';
import { useDialog } from '../lib/ui';
/* The buying roles and the default deal name are the tenant's words, not the product's. */
import { TENANT } from '@tenant';
import './createforms.css';
import { onFail } from '../lib/errors';

function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  // Traps Tab, closes on Escape, returns focus to whatever opened it.
  const ref = useDialog(onClose);
  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label={title} ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cf-body">{children}</div>
      </div>
    </>
  );
}

/** Account picker with inline creation, so a missing counterparty never blocks the task. */
function AccountPicker({ value, onChange, label = 'Counterparty' }: {
  value: string; onChange: (id: string, name: string) => void; label?: string;
}) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [creating, setCreating] = useState(false);
  const [n, setN] = useState({ name: '', kind: 'distributor', country: '' });
  const [err, setErr] = useState<string | null>(null);
  /* An empty list and a failed query used to look identical, because the catch discarded the
     error and set an empty array. Four selects reading "Choose…" with nothing under them is
     indistinguishable from a broken form, and gives no clue which it is. Load state is now
     explicit so the control can say which of the two has happened. */
  const [load, setLoad] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchAccounts()
      .then((a) => { if (live) { setAccounts(a); setLoad('ready'); } })
      .catch((e) => {
        if (!live) return;
        setLoad('failed');
        setLoadErr(e instanceof Error ? e.message : String(e));
      });
    return () => { live = false; };
  }, []);

  async function make() {
    setErr(null);
    try {
      const id = await createAccount({ name: n.name, kinds: [n.kind], country: n.country });
      setAccounts(await fetchAccounts());
      onChange(id, n.name);
      setCreating(false);
      setN({ name: '', kind: 'distributor', country: '' });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <div className="cf-field">
      <span>{label}</span>
      {!creating ? (
        <div className="cf-row">
          <select value={value} disabled={load === 'loading'} onChange={(e) => {
            const a = accounts.find((x) => x.id === e.target.value);
            onChange(e.target.value, a?.name ?? '');
          }}>
            <option value="">
              {load === 'loading' ? 'Loading…'
                : load === 'failed' ? 'Could not load'
                : accounts.length ? 'Choose…'
                : 'None yet. Use New'}
            </option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}{a.country ? ` (${a.country})` : ''}</option>
            ))}
          </select>
          <button type="button" className="btn-compose" onClick={() => setCreating(true)}>New</button>
        </div>
      ) : (
        <div className="cf-inline">
          <input placeholder="Organisation name" value={n.name}
                 onChange={(e) => setN({ ...n, name: e.target.value })} />
          <select value={n.kind} onChange={(e) => setN({ ...n, kind: e.target.value })}>
            {ACCOUNT_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
          <input placeholder="ISO country" value={n.country} style={{ maxWidth: '6rem' }}
                 onChange={(e) => setN({ ...n, country: e.target.value.toUpperCase() })} />
          <button type="button" className="btn-save" disabled={!n.name.trim()} onClick={make}>Create</button>
          <button type="button" className="btn-cancel" onClick={() => setCreating(false)}>Cancel</button>
        </div>
      )}
      {/* Say which of the two happened. A silent empty list reads as a broken form. */}
      {loadErr && <p className="composer-err" role="alert">Counterparties could not be loaded: {loadErr}</p>}
      {err && <p className="composer-err">{err}</p>}
    </div>
  );
}

function Actions({ busy, disabled, onSave, onClose, label }: {
  busy: boolean; disabled: boolean; onSave: () => void; onClose: () => void; label: string;
}) {
  return (
    <div className="cf-actions">
      <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
      <button className="btn-save" onClick={onSave} disabled={busy || disabled}>
        {busy ? 'Saving' : label}
      </button>
    </div>
  );
}

function useSave(onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setErr(null); setBusy(true);
    try { await fn(); onDone(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  return { busy, err, run };
}

/* ---------------------------------- Channel ---------------------------------- */
export function NewPartner({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [accountId, setAccountId] = useState('');
  const [partnerType, setPartnerType] = useState('distributor');
  const { busy, err, run } = useSave(onDone);

  return (
    <Modal title="Add partner" onClose={onClose}>
      <AccountPicker value={accountId} onChange={(id) => setAccountId(id)} label="Which company" />
      <label className="cf-field">
        <span>Role they play</span>
        <select value={partnerType} onChange={(e) => setPartnerType(e.target.value)}>
          {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
      </label>
      <p className="cf-note">
        Starts as <strong>prospective</strong>. A partner becomes active when anti-bribery due
        diligence passes, and the database blocks orders against one whose diligence has
        lapsed. That block is deliberate and cannot be overridden from here.
      </p>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!accountId} label="Add partner" onClose={onClose}
               onSave={() => run(() => createPartner({ accountId, partnerType }))} />
    </Modal>
  );
}

export function NewTerritory({ partners, onClose, onDone }: {
  partners: Array<{ id: string; name: string }>; onClose: () => void; onDone: () => void;
}) {
  const [f, setF] = useState({
    partnerId: '', country: '', exclusivity: 'non_exclusive',
    from: new Date().toISOString().slice(0, 10), to: '', minCommitmentValue: '', noticeDays: 90,
  });
  const { busy, err, run } = useSave(onDone);

  return (
    <Modal title="Grant territory" onClose={onClose}>
      <label className="cf-field">
        <span>Partner</span>
        <select value={f.partnerId} onChange={(e) => setF({ ...f, partnerId: e.target.value })}>
          <option value="">Choose…</option>
          {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <div className="cf-row">
        <label className="cf-field"><span>Country (ISO)</span>
          <input value={f.country} maxLength={3}
                 onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} />
        </label>
        <label className="cf-field"><span>Exclusivity</span>
          <select value={f.exclusivity} onChange={(e) => setF({ ...f, exclusivity: e.target.value })}>
            {EXCLUSIVITY.map((x) => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
      </div>
      <div className="cf-row">
        <label className="cf-field"><span>From</span>
          <input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
        </label>
        <label className="cf-field"><span>Until (blank = open)</span>
          <input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
        </label>
        <label className="cf-field"><span>Notice days</span>
          <input type="number" value={f.noticeDays}
                 onChange={(e) => setF({ ...f, noticeDays: Number(e.target.value) })} />
        </label>
      </div>
      <p className="cf-note">
        Two overlapping <strong>exclusive</strong> grants for the same country and product are
        refused by the database, not by this form. Double-appointing a territory is how
        distributor disputes start, and it is usually discovered years later.
      </p>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!f.partnerId || f.country.length < 2} label="Grant territory"
               onClose={onClose}
               onSave={() => run(() => createTerritoryGrant({
                 partnerId: f.partnerId, country: f.country, exclusivity: f.exclusivity,
                 from: f.from, to: f.to || undefined, noticeDays: f.noticeDays,
                 minCommitmentValue: f.minCommitmentValue ? Number(f.minCommitmentValue) : undefined,
               }))} />
    </Modal>
  );
}

export function NewTender({ partners, onClose, onDone }: {
  partners: Array<{ id: string; name: string }>; onClose: () => void; onDone: () => void;
}) {
  const [f, setF] = useState({
    title: '', authorityName: '', country: 'GBR', reference: '',
    submissionDeadline: '', estimatedValue: '', currency: 'GBP', partnerId: '', portalUrl: '',
  });
  const { busy, err, run } = useSave(onDone);

  return (
    <Modal title="Add tender" onClose={onClose}>
      <label className="cf-field"><span>Title</span>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      </label>
      <div className="cf-row">
        <label className="cf-field"><span>Contracting authority</span>
          <input value={f.authorityName} onChange={(e) => setF({ ...f, authorityName: e.target.value })} />
        </label>
        <label className="cf-field"><span>Country</span>
          <input value={f.country} maxLength={3}
                 onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} />
        </label>
        <label className="cf-field"><span>Reference</span>
          <input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
        </label>
      </div>
      <div className="cf-row">
        <label className="cf-field"><span>Submission deadline</span>
          <input type="date" value={f.submissionDeadline}
                 onChange={(e) => setF({ ...f, submissionDeadline: e.target.value })} />
        </label>
        <label className="cf-field"><span>Estimated value</span>
          <input type="number" value={f.estimatedValue}
                 onChange={(e) => setF({ ...f, estimatedValue: e.target.value })} />
        </label>
        {/* Bidding directly is a real option, so "no partner" is a valid answer rather than
            a gap in the form. */}
        <label className="cf-field"><span>Through a partner?</span>
          <select value={f.partnerId} onChange={(e) => setF({ ...f, partnerId: e.target.value })}>
            <option value="">We bid directly</option>
            {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </div>
      <label className="cf-field"><span>Portal URL</span>
        <input value={f.portalUrl} onChange={(e) => setF({ ...f, portalUrl: e.target.value })} />
      </label>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!f.title.trim() || !f.authorityName.trim()} label="Add tender"
               onClose={onClose}
               onSave={() => run(() => createTender({
                 ...f, estimatedValue: f.estimatedValue ? Number(f.estimatedValue) : undefined,
                 partnerId: f.partnerId || null,
               }))} />
    </Modal>
  );
}

/* ---------------------------------- Studio ---------------------------------- */
export function NewAsset({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    jobCode: '', title: '', assetType: 'brochure', territories: '', audience: '', description: '',
  });
  const { busy, err, run } = useSave(onDone);

  return (
    <Modal title="New asset" onClose={onClose}>
      <div className="cf-row">
        <label className="cf-field"><span>Job code</span>
          <input placeholder="SC-2026-001" value={f.jobCode}
                 onChange={(e) => setF({ ...f, jobCode: e.target.value })} />
        </label>
        <label className="cf-field"><span>Type</span>
          <select value={f.assetType} onChange={(e) => setF({ ...f, assetType: e.target.value })}>
            {ASSET_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
      </div>
      <label className="cf-field"><span>Title</span>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      </label>
      <div className="cf-row">
        {/* Territories drive the claim gate: a claim allowed in one market may exceed the
            product's regulatory status in another, and the gate checks every destination. */}
        <label className="cf-field"><span>Territories (ISO, comma separated)</span>
          <input placeholder="GBR, ITA, USA" value={f.territories}
                 onChange={(e) => setF({ ...f, territories: e.target.value })} />
        </label>
        <label className="cf-field"><span>Audience</span>
          <input placeholder="Clinicians, laboratory, payer" value={f.audience}
                 onChange={(e) => setF({ ...f, audience: e.target.value })} />
        </label>
      </div>
      <p className="cf-note">
        Created as a <strong>draft</strong>. Claim level is set on each version rather than the
        asset, because version 2 may claim more than version 1 and the gate must judge the
        version actually in review.
      </p>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!f.jobCode.trim() || !f.title.trim()} label="Create asset"
               onClose={onClose}
               onSave={() => run(() => createAsset({
                 jobCode: f.jobCode, title: f.title, assetType: f.assetType,
                 description: f.description, audience: f.audience,
                 territories: f.territories.split(',').map((t) => t.trim()).filter(Boolean),
               }))} />
    </Modal>
  );
}

/* ---------------------------------- Contracts ---------------------------------- */
export function NewContract({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    reference: '', title: '', contractType: 'distribution',
    counterpartyAccountId: '', counterpartyName: '',
    value: '', currency: 'GBP', effectiveFrom: '',
    initialTermMonths: '', noticePeriodDays: '', paymentTermsDays: 30, governingLaw: 'England and Wales',
  });
  const { busy, err, run } = useSave(onDone);

  return (
    <Modal title="Add contract" onClose={onClose}>
      <div className="cf-row">
        <label className="cf-field"><span>Reference</span>
          <input placeholder="SC-DIST-001" value={f.reference}
                 onChange={(e) => setF({ ...f, reference: e.target.value })} />
        </label>
        <label className="cf-field"><span>Type</span>
          <select value={f.contractType} onChange={(e) => setF({ ...f, contractType: e.target.value })}>
            {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
      <label className="cf-field"><span>Title</span>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      </label>
      <AccountPicker value={f.counterpartyAccountId}
                     onChange={(id, name) => setF({ ...f, counterpartyAccountId: id, counterpartyName: name })} />
      <div className="cf-row">
        <label className="cf-field"><span>Value</span>
          <input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} />
        </label>
        <label className="cf-field"><span>Currency</span>
          <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            <option>GBP</option><option>EUR</option><option>USD</option>
          </select>
        </label>
        <label className="cf-field"><span>Payment terms (days)</span>
          <input type="number" value={f.paymentTermsDays}
                 onChange={(e) => setF({ ...f, paymentTermsDays: Number(e.target.value) })} />
        </label>
      </div>
      <div className="cf-row">
        <label className="cf-field"><span>Effective from</span>
          <input type="date" value={f.effectiveFrom}
                 onChange={(e) => setF({ ...f, effectiveFrom: e.target.value })} />
        </label>
        <label className="cf-field"><span>Initial term (months)</span>
          <input type="number" value={f.initialTermMonths}
                 onChange={(e) => setF({ ...f, initialTermMonths: e.target.value })} />
        </label>
        {/* The notice deadline is computed by the database from the term and this figure. It
            is never typed, because a missed notice window auto-renews a contract nobody
            wanted for another full term. */}
        <label className="cf-field"><span>Notice period (days)</span>
          <input type="number" value={f.noticePeriodDays}
                 onChange={(e) => setF({ ...f, noticePeriodDays: e.target.value })} />
        </label>
      </div>
      <p className="cf-note">
        Starts as <strong>draft</strong>, then moves through final, out for signature, partially
        executed and fully executed. Partially executed means one side has signed and the other
        has not: they hold an option, you hold an obligation. Forum will chase you for a fully
        executed copy until one is attached.
      </p>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!f.reference.trim() || !f.title.trim() || !f.counterpartyName}
               label="Create contract" onClose={onClose}
               onSave={() => run(() => createContract({
                 reference: f.reference, title: f.title, contractType: f.contractType,
                 counterpartyName: f.counterpartyName,
                 counterpartyAccountId: f.counterpartyAccountId || null,
                 value: f.value ? Number(f.value) : undefined,
                 currency: f.currency,
                 effectiveFrom: f.effectiveFrom || undefined,
                 initialTermMonths: f.initialTermMonths ? Number(f.initialTermMonths) : undefined,
                 noticePeriodDays: f.noticePeriodDays ? Number(f.noticePeriodDays) : undefined,
                 paymentTermsDays: f.paymentTermsDays,
                 governingLaw: f.governingLaw,
               }))} />
    </Modal>
  );
}

/* ---------------------------------- Ledger ---------------------------------- */
export function NewInvoice({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    number: '', accountId: '', accountName: '', currency: 'GBP',
    subtotal: '', taxRate: 20, dueDays: 30, notes: '',
  });
  const { busy, err, run } = useSave(onDone);

  // Sequential and gapless per year. Missing numbers are a question at audit.
  useEffect(() => { nextInvoiceNumber().then((n) => setF((p) => ({ ...p, number: n }))); }, []);

  const sub = Number(f.subtotal || 0);
  const tax = Math.round(sub * (f.taxRate / 100) * 100) / 100;
  const due = new Date(Date.now() + f.dueDays * 86_400_000).toISOString().slice(0, 10);

  return (
    <Modal title="New invoice" onClose={onClose}>
      <div className="cf-row">
        <label className="cf-field"><span>Number</span>
          <input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} />
        </label>
        <label className="cf-field"><span>Currency</span>
          <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            <option>GBP</option><option>EUR</option><option>USD</option>
          </select>
        </label>
      </div>
      <AccountPicker value={f.accountId} label="Bill to"
                     onChange={(id, name) => setF({ ...f, accountId: id, accountName: name })} />
      <div className="cf-row">
        <label className="cf-field"><span>Net amount</span>
          <input type="number" value={f.subtotal}
                 onChange={(e) => setF({ ...f, subtotal: e.target.value })} />
        </label>
        <label className="cf-field"><span>VAT %</span>
          <input type="number" value={f.taxRate}
                 onChange={(e) => setF({ ...f, taxRate: Number(e.target.value) })} />
        </label>
        <label className="cf-field"><span>Due in (days)</span>
          <input type="number" value={f.dueDays}
                 onChange={(e) => setF({ ...f, dueDays: Number(e.target.value) })} />
        </label>
      </div>
      <div className="cf-total">
        <span>Net {sub.toFixed(2)}</span>
        <span>VAT {tax.toFixed(2)}</span>
        <strong>Total {(sub + tax).toFixed(2)} {f.currency}</strong>
        <span className="muted">due {due}</span>
      </div>
      <p className="cf-note">
        Created as a <strong>draft</strong>. Issuing is a separate, deliberate step because an
        issued invoice is append-only: it can be credited but never edited, which is what makes
        the ledger trustworthy.
      </p>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!f.number.trim() || !f.accountId || !sub}
               label="Create draft invoice" onClose={onClose}
               onSave={() => run(() => createInvoice({
                 number: f.number, accountId: f.accountId, dueDate: due,
                 currency: f.currency, subtotal: sub, taxTotal: tax, notes: f.notes,
               }))} />
    </Modal>
  );
}

/* ---------------------------------- Pipeline ---------------------------------- */

/**
 * A transplant diagnostics opportunity has four parties and they are rarely the same
 * organisation: the centre that wants the test, the laboratory that holds the budget and
 * signs, the commissioner that funds it, and the procurement body that runs the process.
 * Selling to the first and ignoring the second loses the order, so all four are on the form.
 */
export function NewOpportunity({ siteId, siteName, onClose, onDone }: {
  siteId?: string | null; siteName?: string; onClose: () => void; onDone: () => void;
}) {
  const [stages, setStages] = useState<Stage[]>([]);
  const [f, setF] = useState({
    name: siteName ? `${siteName}: ${TENANT.vocabulary.defaultDealName}` : '',
    stageId: '', amount: '', currency: 'GBP', expectedClose: '',
    demandAccountId: '', buyingAccountId: '', payerAccountId: '', procurementAccountId: '',
    nextStep: '',
  });
  const { busy, err, run } = useSave(onDone);

  useEffect(() => {
    fetchStages().then((s) => {
      setStages(s);
      if (s.length) setF((p) => ({ ...p, stageId: p.stageId || s[0].id }));
    }).catch(onFail('Pipeline stages', () => setStages([])));
  }, []);

  return (
    <Modal title="New opportunity" onClose={onClose}>
      <label className="cf-field"><span>Name</span>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      </label>
      <div className="cf-row">
        <label className="cf-field"><span>Stage</span>
          <select value={f.stageId} onChange={(e) => setF({ ...f, stageId: e.target.value })}>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.probability}%)</option>
            ))}
          </select>
        </label>
        <label className="cf-field"><span>Value</span>
          <input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        </label>
        <label className="cf-field"><span>Currency</span>
          <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            <option>GBP</option><option>EUR</option><option>USD</option>
          </select>
        </label>
        <label className="cf-field"><span>Expected close</span>
          <input type="date" value={f.expectedClose}
                 onChange={(e) => setF({ ...f, expectedClose: e.target.value })} />
        </label>
      </div>

      <p className="cf-note">
        Four parties, rarely the same organisation. The centre creates the demand, the
        laboratory usually holds the reagent budget and signs, the commissioner funds it and
        procurement runs the process. Any of them can stop the sale.
      </p>

      <AccountPicker label={TENANT.vocabulary.buyingRoles.demand} value={f.demandAccountId}
                     onChange={(id) => setF({ ...f, demandAccountId: id })} />
      <AccountPicker label={TENANT.vocabulary.buyingRoles.buying} value={f.buyingAccountId}
                     onChange={(id) => setF({ ...f, buyingAccountId: id })} />
      <AccountPicker label={TENANT.vocabulary.buyingRoles.funding} value={f.payerAccountId}
                     onChange={(id) => setF({ ...f, payerAccountId: id })} />
      <AccountPicker label={TENANT.vocabulary.buyingRoles.process} value={f.procurementAccountId}
                     onChange={(id) => setF({ ...f, procurementAccountId: id })} />

      <label className="cf-field"><span>Next step</span>
        <input placeholder="What has to happen next, and by when" value={f.nextStep}
               onChange={(e) => setF({ ...f, nextStep: e.target.value })} />
      </label>

      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!f.name.trim()} label="Create opportunity" onClose={onClose}
               onSave={() => run(() => createOpportunity({
                 name: f.name, stageId: f.stageId || null,
                 amount: f.amount ? Number(f.amount) : undefined,
                 currency: f.currency,
                 expectedClose: f.expectedClose || undefined,
                 siteId: siteId || null,
                 demandAccountId: f.demandAccountId || null,
                 buyingAccountId: f.buyingAccountId || null,
                 payerAccountId: f.payerAccountId || null,
                 procurementAccountId: f.procurementAccountId || null,
                 nextStep: f.nextStep,
               }))} />
    </Modal>
  );
}

export function LogInteraction({ siteId, siteName, accountId, opportunityId, onClose, onDone }: {
  siteId?: string | null; siteName?: string; accountId?: string | null;
  opportunityId?: string | null; onClose: () => void; onDone: () => void;
}) {
  const [f, setF] = useState({
    kind: 'meeting', subject: '', body: '',
    occurredAt: new Date().toISOString().slice(0, 10),
    sentiment: 'neutral', followUpRequired: false, location: '',
  });
  const { busy, err, run } = useSave(onDone);

  return (
    <Modal title={siteName ? `Log interaction: ${siteName}` : 'Log interaction'} onClose={onClose}>
      <div className="cf-row">
        <label className="cf-field"><span>Kind</span>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
            {INTERACTION_KINDS.map((k) => (
              <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>
        {/* Back-dating is the normal case: a congress conversation gets written up days
            later and must carry the date it happened. */}
        <label className="cf-field"><span>When it happened</span>
          <input type="date" value={f.occurredAt}
                 onChange={(e) => setF({ ...f, occurredAt: e.target.value })} />
        </label>
        <label className="cf-field"><span>Where</span>
          <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
        </label>
      </div>
      <label className="cf-field"><span>Subject</span>
        <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
      </label>
      <label className="cf-field"><span>What was said</span>
        <textarea rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      </label>
      <div className="cf-row">
        <label className="cf-field"><span>How it went</span>
          <select value={f.sentiment} onChange={(e) => setF({ ...f, sentiment: e.target.value })}>
            {SENTIMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="cf-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '.4rem' }}>
          <input type="checkbox" checked={f.followUpRequired} style={{ width: 'auto' }}
                 onChange={(e) => setF({ ...f, followUpRequired: e.target.checked })} />
          <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: '.85rem' }}>
            Needs a follow-up
          </span>
        </label>
      </div>
      <p className="cf-note">
        This is a business record: searchable, audited, retained, and disclosable in a subject
        access request if it names a person. Record what was said, not what you thought of them.
      </p>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <Actions busy={busy} disabled={!f.subject.trim()} label="Log it" onClose={onClose}
               onSave={() => run(() => logInteraction({
                 kind: f.kind, subject: f.subject, body: f.body,
                 occurredAt: f.occurredAt, siteId: siteId || null,
                 accountId: accountId || null, opportunityId: opportunityId || null,
                 sentiment: f.sentiment, followUpRequired: f.followUpRequired,
                 location: f.location,
               }))} />
    </Modal>
  );
}
